import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;

const Shell = ({ testId, kicker, title, children }) => (
  <main className="pt-36 pb-28 max-w-[1440px] mx-auto px-5 sm:px-10 min-h-[80svh]" data-testid={testId}>
    <Seo title={`${title} | YOUTH`} noindex />
    <p className="kicker text-ink/70">{kicker}</p>
    <h1 className="dz-type text-[clamp(2.6rem,7vw,6.5rem)] mt-4 max-w-5xl">{title}</h1>
    <div className="mt-10 max-w-2xl font-serif text-xl leading-snug">{children}</div>
  </main>
);

const DownloadButton = ({ href }) => (
  <a href={`${API}/api/shop${href}`} className="mt-8 inline-flex items-center gap-3 bg-ink text-cream kicker px-7 py-4 border-2 border-ink hover:bg-pink hover:text-ink transition-colors" data-testid="download-pdf-btn">
    <Download className="h-4 w-4" /> Download the PDF
  </a>
);

// Back from Stripe: wait until the payment is confirmed, then hand over the download.
export function PaymentSuccess() {
  const [params] = useSearchParams();
  const id = params.get("session_id");
  const [st, setSt] = useState({ payment_status: "pending" });
  useEffect(() => {
    if (!id) return undefined;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${id}`);
        setSt(data);
        if (data.payment_status === "paid" && data.download_url) return;
        if (["expired", "failed"].includes(data.payment_status)) return;
      } catch {
        setSt({ payment_status: "error" });
        return;
      }
      tries += 1;
      if (tries < 30) timer = setTimeout(poll, 2000);
      else setSt((s) => ({ ...s, payment_status: "timeout" }));
    };
    poll();
    return () => clearTimeout(timer);
  }, [id]);
  if (st.payment_status === "paid" && st.download_url)
    return (
      <Shell testId="payment-success" kicker="Payment received" title="The issue is yours.">
        <p>Thank you. Download your PDF below. We have also emailed you the link: it works for 30 days.</p>
        <DownloadButton href={st.download_url} />
      </Shell>
    );
  if (["expired", "failed", "error", "timeout"].includes(st.payment_status) || !id)
    return (
      <Shell testId="payment-problem" kicker="Payment" title="We couldn't confirm the payment.">
        <p>If you were charged, check your email for the download link, or write to us and we'll sort it out.</p>
        <Link to="/" className="kicker underline mt-6 inline-block" data-testid="payment-home-link">Back to the magazine</Link>
      </Shell>
    );
  return (
    <Shell testId="payment-pending" kicker="Payment" title="Confirming your payment…">
      <p>This takes a few seconds. Don't close this page.</p>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testId="payment-cancel" kicker="Payment" title="No payment was made.">
      <p>You can still flip through the first pages of the issue for free, and buy the PDF whenever you like.</p>
      <Link to="/" className="kicker underline mt-6 inline-block" data-testid="payment-cancel-home-link">Back to the magazine</Link>
    </Shell>
  );
}

// The link from the email.
export function DownloadPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    api.get(`/shop/download/${token}/info`).then(({ data }) => setInfo(data)).catch(() => setBad(true));
  }, [token]);
  if (bad)
    return (
      <Shell testId="download-invalid" kicker="Download" title="This link doesn't work any more.">
        <p>Download links last 30 days. Write to us with your payment email and we'll send a new one.</p>
      </Shell>
    );
  if (!info) return <Shell testId="download-loading" kicker="Download" title="One moment…" />;
  return (
    <Shell testId="download-page" kicker="Your issue" title={info.title}>
      <p>Your printable PDF is ready. The link works until {new Date(info.expires_at).toLocaleDateString()}.</p>
      {info.left > 0 ? <DownloadButton href={`/download/${token}`} /> : <p className="mt-6">This link has reached its download limit.</p>}
    </Shell>
  );
}
